import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { authApi } from 'auth/auth.api';

import { refreshAccessToken } from 'auth/sessionRefresh';

import { startSessionKeepAlive, stopSessionKeepAlive } from 'auth/sessionKeepAlive';

import { hasAnyPermission, hasPermission as checkPermission, normalizeAdmin, PERMISSIONS } from 'auth/permissions';

import {

  clearAdminSession,

  getAccessToken,

  getRefreshToken,

  hasStoredAdminSession,

  isAccessTokenExpired,

  subscribeAdminSessionSync,

} from 'auth/tokenStore';

import { clearExpectedRevision, captureRevision } from 'core/api/revisionStore';

import { queryKeys } from 'core/api/queryKeys';

import { onSessionExpired } from 'core/api/sessionEvents';

import { ApiError } from 'core/errors/ApiError';

import { assessmentApi } from 'features/assessment-dashboard/assessment.api';



function isAbortError(error) {

  return error?.name === 'CanceledError'

    || error?.code === 'ERR_CANCELED'

    || error?.name === 'AbortError';

}



function isTemporaryBootstrapError(error) {

  return error instanceof ApiError && (error.isNetworkError || error.status >= 500);

}



const AuthContext = createContext(null);



const REVISION_PERMISSIONS = [

  PERMISSIONS.view,

  PERMISSIONS.patternsManage,

  PERMISSIONS.sectionsManage,

  PERMISSIONS.questionsManage,

  PERMISSIONS.weightsManage,

  PERMISSIONS.versionsManage,

];



export function AuthProvider({ children }) {

  const queryClient = useQueryClient();

  const [admin, setAdmin] = useState(null);

  const [status, setStatus] = useState('bootstrapping');

  const [sessionMessage, setSessionMessage] = useState(null);

  const adminIdRef = useRef(null);

  const bootstrapControllerRef = useRef(null);



  const hydrateRevision = useCallback(async (adminOrPermissions) => {

    if (!hasAnyPermission(adminOrPermissions, REVISION_PERMISSIONS)) {

      return;

    }

    try {

      const data = await queryClient.fetchQuery({

        queryKey: queryKeys.overview,

        queryFn: () => assessmentApi.overview(),

      });

      captureRevision(data);

    } catch {

      // Overview may be forbidden or temporarily unavailable; mutations fetch when needed.

    }

  }, [queryClient]);



  const clearSession = useCallback((message = null) => {

    clearAdminSession();

    clearExpectedRevision();

    queryClient.clear();

    adminIdRef.current = null;

    setAdmin(null);

    setStatus('anonymous');

    setSessionMessage(message);

  }, [queryClient]);



  const applyAdmin = useCallback(async (nextAdmin) => {

    const normalized = normalizeAdmin(nextAdmin);

    if (adminIdRef.current && normalized?.id && adminIdRef.current !== normalized.id) {

      queryClient.clear();

      clearExpectedRevision();

    }

    adminIdRef.current = normalized?.id ?? null;

    setAdmin(normalized);

    setStatus('authenticated');

    setSessionMessage(null);

    await hydrateRevision(normalized);

  }, [hydrateRevision, queryClient]);



  const restoreSession = useCallback(async ({ signal } = {}) => {

    if (!hasStoredAdminSession()) {

      setStatus('anonymous');

      return;

    }



    try {

      if (!getAccessToken() || isAccessTokenExpired()) {

        const refreshed = await refreshAccessToken({ force: true });

        if (!refreshed && !getAccessToken()) {

          if (!getRefreshToken()) {

            clearSession('auth.sessionExpired');

          } else {

            setStatus('temporarily_unavailable');

          }

          return;

        }

      } else {

        try {

          await refreshAccessToken();

        } catch {

          // A proactive refresh failure falls through to /admin/me.

        }

      }



      if (!getAccessToken()) {

        setStatus('temporarily_unavailable');

        return;

      }



      const loadProfile = async () => authApi.me({ signal });

      let data;



      try {

        data = await loadProfile();

      } catch (error) {

        if (!(error instanceof ApiError && error.status === 401)) {

          throw error;

        }

        const refreshed = await refreshAccessToken({ force: true });

        if (!refreshed) {

          clearSession('auth.sessionExpired');

          return;

        }

        data = await loadProfile();

      }



      await applyAdmin(data);

    } catch (error) {

      if (isAbortError(error)) {

        return;

      }

      if (error instanceof ApiError && error.status === 403) {

        setAdmin(null);

        setStatus('forbidden');

        return;

      }

      if (error instanceof ApiError && error.status === 401) {

        clearSession('auth.sessionExpired');

        return;

      }

      if (isTemporaryBootstrapError(error)) {

        setStatus('temporarily_unavailable');

        return;

      }

      setStatus('temporarily_unavailable');

    }

  }, [applyAdmin, clearSession]);



  const retryBootstrap = useCallback(async () => {

    bootstrapControllerRef.current?.abort();

    const controller = new AbortController();

    bootstrapControllerRef.current = controller;

    setStatus('bootstrapping');

    await restoreSession({ signal: controller.signal });

  }, [restoreSession]);



  useEffect(() => {

    const controller = new AbortController();

    bootstrapControllerRef.current = controller;

    restoreSession({ signal: controller.signal });

    return () => controller.abort();

  }, [restoreSession]);



  useEffect(() => onSessionExpired(() => clearSession('auth.sessionExpired')), [clearSession]);



  useEffect(() => subscribeAdminSessionSync(() => clearSession('auth.sessionExpired')), [clearSession]);



  useEffect(() => {

    if (status !== 'authenticated') {

      stopSessionKeepAlive();

      return undefined;

    }

    return startSessionKeepAlive(refreshAccessToken);

  }, [status]);



  const login = useCallback(async ({ email, password }) => {

    await authApi.login({ email, password });

    const me = await authApi.me();

    const adminProfile = normalizeAdmin(me);

    await applyAdmin(adminProfile);

    return { admin: adminProfile };

  }, [applyAdmin]);



  const logout = useCallback(async () => {

    try {

      await authApi.logout();

    } catch {

      // A 401/network failure from /admin/logout must not block local sign-out.

    } finally {

      clearAdminSession();

      clearExpectedRevision();

      queryClient.clear();

      adminIdRef.current = null;

      setAdmin(null);

      setStatus('anonymous');

      setSessionMessage(null);

    }

  }, [queryClient]);



  const value = useMemo(() => {

    const permissions = admin?.permissions || [];

    return {

      admin,

      status,

      sessionMessage,

      permissions,

      authorizationReady: status === 'authenticated' && Boolean(admin),

      isAuthenticated: status === 'authenticated',

      hasPermission: (slug) => checkPermission(admin, slug),

      hasAnyPermission: (slugs) => hasAnyPermission(admin, slugs),

      login,

      logout,

      clearSession,

      retryBootstrap,

    };

  }, [admin, clearSession, login, logout, retryBootstrap, sessionMessage, status]);



  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;

}



export function useAuth() {

  const context = useContext(AuthContext);

  if (!context) {

    throw new Error('useAuth must be used within AuthProvider');

  }

  return context;

}

